import { llmRequest } from "./client";
import { DOCUMENT_GENERATION_SYSTEM_PROMPT } from "./prompts";

export interface RoleEntry {
  title: string;
  organisation: string;
  period: string;
  description: string;
  bullets: string[];
  result: string;
  tech: string;
}

export interface EducationEntry {
  degree: string;
  institution: string;
  year: string;
}

export interface TailoredCVContent {
  document_type: "tailored_cv";
  language: string;
  role_title: string;
  profile_text: string;
  competencies: string[];
  tech_stack: Record<string, string>;
  roles: RoleEntry[];
  education: EducationEntry[];
  certifications: string[];
  tailoring_notes: string;
  known_gaps_not_resolved: string[];
}

export interface MotivatiebriefContent {
  document_type: "motivatiebrief";
  language: string;
  salutation: string;
  opening_paragraph: string;
  body_paragraphs: string[];
  closing_paragraph: string;
  sign_off: string;
}

export type DocumentContent = TailoredCVContent | MotivatiebriefContent;

export interface GenerateDocumentParams {
  document_type: "tailored_cv" | "motivatiebrief";
  language?: string;
  cv_text: string;
  profile_json: string;
  vacancy_json: string;
  lens_notes?: string;
}

const roleSchema = {
  type: "object",
  properties: {
    title:        { type: "string" },
    organisation: { type: "string" },
    period:       { type: "string" },
    description:  { type: "string" },
    bullets:      { type: "array", items: { type: "string" } },
    result:       { type: "string" },
    tech:         { type: "string" },
  },
  required: ["title", "organisation", "period", "description", "bullets", "result", "tech"],
};

const educationSchema = {
  type: "object",
  properties: {
    degree:      { type: "string" },
    institution: { type: "string" },
    year:        { type: "string" },
  },
  required: ["degree", "institution", "year"],
};

const cvSchema = {
  type: "object",
  properties: {
    document_type:           { type: "string", enum: ["tailored_cv"] },
    language:                { type: "string" },
    role_title:              { type: "string" },
    profile_text:            { type: "string" },
    competencies:            { type: "array", items: { type: "string" } },
    tech_stack: {
      type: "array",
      items: {
        type: "object",
        properties: {
          category: { type: "string" },
          technologies: { type: "string" },
        },
        required: ["category", "technologies"],
      },
    },
    roles:                   { type: "array", items: roleSchema },
    education:               { type: "array", items: educationSchema },
    certifications:          { type: "array", items: { type: "string" } },
    tailoring_notes:         { type: "string" },
    known_gaps_not_resolved: { type: "array", items: { type: "string" } },
  },
  required: [
    "document_type", "language", "role_title", "profile_text",
    "competencies", "tech_stack", "roles", "education",
    "certifications", "tailoring_notes", "known_gaps_not_resolved",
  ],
};

const letterSchema = {
  type: "object",
  properties: {
    document_type:     { type: "string", enum: ["motivatiebrief"] },
    language:          { type: "string" },
    salutation:        { type: "string" },
    opening_paragraph: { type: "string" },
    body_paragraphs:   { type: "array", items: { type: "string" } },
    closing_paragraph: { type: "string" },
    sign_off:          { type: "string" },
  },
  required: [
    "document_type", "language", "salutation",
    "opening_paragraph", "body_paragraphs", "closing_paragraph", "sign_off",
  ],
};

export async function generateDocument(
  _apiKey: string,   // kept for call-site compat during transition; ignored
  _model: string,
  params: GenerateDocumentParams,
  systemPrompt?: string | null,
): Promise<DocumentContent> {
  const schema = params.document_type === "tailored_cv" ? cvSchema : letterSchema;

  const userPrompt =
    `Document type: ${params.document_type}\n` +
    `Language: ${params.language ?? "nl"}\n\n` +
    `Vacancy (JSON):\n${params.vacancy_json}\n\n` +
    `Consultant CV text:\n${params.cv_text}\n\n` +
    `Consultant profile (JSON):\n${params.profile_json}\n\n` +
    (params.lens_notes ? `Framing notes from recruiter:\n${params.lens_notes}\n\n` : "") +
    `Generate the ${params.document_type} document content in Dutch.`;

  const raw = await llmRequest<any>({
    systemPrompt: systemPrompt || DOCUMENT_GENERATION_SYSTEM_PROMPT,
    userContent: [{ type: "text", text: userPrompt }],
    schema,
    maxTokens: 8000,
    temperature: 0.3,
  });

  return parseAndReconstructCv(raw);
}

// Gemini returns tech_stack as an array of {category, technologies} objects;
// reconstruct into the Record<string, string> the rest of the app expects.
function parseAndReconstructCv(parsed: any): DocumentContent {
  if (parsed.document_type === "tailored_cv" && Array.isArray(parsed.tech_stack)) {
    const record: Record<string, string> = {};
    for (const item of parsed.tech_stack) {
      if (item && typeof item === "object" && item.category) {
        record[item.category] = item.technologies || "";
      }
    }
    parsed.tech_stack = record;
  }
  return parsed as DocumentContent;
}
